// Network paths for visualising a message. A note travels:
//   player → the Cloudflare data center their connection lands on → the Durable
//   Object's home data center → every listener's data center → every listener.
// Leg durations are the real speed-of-light-in-fiber delay, slowed down by a
// constant factor so human eyes can follow it.

import { coloByIata } from "../shared/colos";
import { distanceKm, fiberLatencyMs, type LatLon } from "../shared/geo";
import type { PlayerInfo, ServerHome } from "../shared/protocol";

/** How much slower than light the visualisation runs. */
export const SLOWDOWN = 22;
const MIN_LEG_MS = 140;
const MAX_LEG_MS = 1500;

export interface Leg {
  from: LatLon;
  to: LatLon;
  realMs: number;
  visualMs: number;
}

export interface Route {
  points: LatLon[];
  legs: Leg[];
  realMs: number;
  visualMs: number;
}

function leg(from: LatLon, to: LatLon): Leg {
  const realMs = fiberLatencyMs(from, to);
  const visualMs = Math.min(MAX_LEG_MS, Math.max(MIN_LEG_MS, realMs * SLOWDOWN));
  return { from, to, realMs, visualMs };
}

/** Builds a route through waypoints, dropping hops that are essentially zero length. */
export function route(points: LatLon[]): Route {
  const pts: LatLon[] = [];
  for (const p of points) {
    const last = pts[pts.length - 1];
    if (!last || distanceKm(last, p) > 25) pts.push(p);
  }
  const legs: Leg[] = [];
  for (let i = 1; i < pts.length; i++) legs.push(leg(pts[i - 1]!, pts[i]!));
  return {
    points: pts,
    legs,
    realMs: legs.reduce((s, l) => s + l.realMs, 0),
    visualMs: legs.reduce((s, l) => s + l.visualMs, 0),
  };
}

export function coloPoint(iata: string, fallback: LatLon): LatLon {
  const c = coloByIata(iata);
  return c ? { lat: c.lat, lon: c.lon } : fallback;
}

/** Player → their edge → the Durable Object. */
export function uplink(p: Pick<PlayerInfo, "lat" | "lon" | "colo">, home: ServerHome): Route {
  const me = { lat: p.lat, lon: p.lon };
  return route([me, coloPoint(p.colo, me), { lat: home.lat, lon: home.lon }]);
}

/** Durable Object → listener's edge → listener. */
export function downlink(home: ServerHome, p: Pick<PlayerInfo, "lat" | "lon" | "colo">): Route {
  const them = { lat: p.lat, lon: p.lon };
  return route([{ lat: home.lat, lon: home.lon }, coloPoint(p.colo, them), them]);
}

/** Round trip for a player, as the physics would have it. */
export function theoreticalRttMs(p: Pick<PlayerInfo, "lat" | "lon" | "colo">, home: ServerHome): number {
  return uplink(p, home).realMs * 2;
}
